"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { LoaderCircle, Newspaper, Search, User, Users } from "lucide-react";

import { useCurrentUser } from "@/hooks/use-current-user";
import { usePlatformSearch } from "@/hooks/use-platform-search";
import {
  searchResultCount,
  type PlatformSearchHit,
  type PlatformSearchResults,
} from "@/lib/search/types";
import { getErrorMessage } from "@/lib/utils";

const SECTIONS: {
  key: keyof PlatformSearchResults;
  label: string;
  icon: typeof Newspaper;
}[] = [
  { key: "members", label: "Tagok", icon: User },
  { key: "communities", label: "Közösségek", icon: Users },
  { key: "posts", label: "Bejegyzések", icon: Newspaper },
];

export function GlobalSearch() {
  const router = useRouter();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const currentUserQuery = useCurrentUser();
  const enabled = currentUserQuery.data?.accountStatus === "active";
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const searchQuery = usePlatformSearch(query, enabled && open);
  const results = searchQuery.data;
  const trimmed = query.trim();
  const showPanel = open && trimmed.length > 0;
  const total = results ? searchResultCount(results) : 0;
  const firstHref = useMemo(() => firstResultHref(results), [results]);

  useEffect(() => {
    if (!showPanel) {
      return;
    }

    function handlePointer(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    }

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [showPanel]);

  function goTo(href: string) {
    setOpen(false);
    setQuery("");
    router.push(href);
  }

  return (
    <div ref={rootRef} className="relative mx-auto hidden w-full max-w-xl md:block">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={inputRef}
        type="search"
        value={query}
        placeholder="Keresés a bejegyzésekben, csoportokban, tagokban..."
        className="h-10 w-full rounded-full border-0 bg-muted/80 pl-10 pr-4 text-sm outline-none ring-1 ring-transparent transition placeholder:text-muted-foreground focus:bg-card focus:ring-primary/30"
        autoComplete="off"
        aria-label="Keresés"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showPanel}
        aria-controls="global-search-results"
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || !firstHref) {
            return;
          }
          event.preventDefault();
          goTo(firstHref);
        }}
      />

      {showPanel ? (
        <div
          id="global-search-results"
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-lg"
        >
          <div className="max-h-96 overflow-y-auto p-1.5">
            {!enabled ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                A kereséshez aktív PraxisTárs fiók szükséges.
              </p>
            ) : trimmed.length < 2 ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                Írjon be legalább két karaktert.
              </p>
            ) : !results && (searchQuery.isLoading || searchQuery.isFetching) ? (
              <p className="flex items-center justify-center gap-2 px-3 py-8 text-sm text-muted-foreground">
                <LoaderCircle className="size-4 animate-spin" />
                Keresés…
              </p>
            ) : searchQuery.isError ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                {getErrorMessage(searchQuery.error)}
              </p>
            ) : total === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                Nincs találat.
              </p>
            ) : (
              SECTIONS.map((section) => {
                const hits = results?.[section.key] ?? [];
                if (hits.length === 0) {
                  return null;
                }

                const Icon = section.icon;
                return (
                  <section key={section.key} className="py-1">
                    <p className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      {section.label}
                    </p>
                    <ul>
                      {hits.map((hit) => (
                        <li key={`${section.key}-${hit.id}`}>
                          <Link
                            href={hit.href}
                            role="option"
                            className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-sm hover:bg-muted/80"
                            onClick={() => {
                              setOpen(false);
                              setQuery("");
                            }}
                          >
                            <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                            <span className="min-w-0">
                              <span className="block truncate font-medium">
                                {hit.title}
                              </span>
                              {hit.subtitle ? (
                                <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                                  {hit.subtitle}
                                </span>
                              ) : null}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function firstResultHref(results: PlatformSearchResults | undefined) {
  if (!results) {
    return null;
  }

  for (const section of SECTIONS) {
    const hit = results[section.key][0] as PlatformSearchHit | undefined;
    if (hit) {
      return hit.href;
    }
  }

  return null;
}
